import * as React from "react";
import { cn } from "@/lib/utils";
import { Twitter, Linkedin, Github } from "lucide-react";

interface FooterLink {
  label: string;
  href: string;
}

interface FooterNavSection {
  title: string;
  links: FooterLink[];
}

interface SocialLink {
  platform: string;
  url: string;
}

interface FooterProps {
  company: {
    name: string;
    description: string;
    copyright: string;
  };
  navigation: FooterNavSection[];
  social?: SocialLink[];
}

const socialIcons: Record<string, React.ElementType> = {
  twitter: Twitter,
  linkedin: Linkedin,
  github: Github,
};

export function Footer({ company, navigation, social }: FooterProps) {
  return (
    <footer className="relative bg-muted/50 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-12 lg:gap-8">
          {/* Company info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-white font-bold text-sm">ES</span>
              </div>
              <span className="text-foreground font-semibold text-lg">
                {company.name}
              </span>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6 max-w-xs">
              {company.description}
            </p>

            {social && social.length > 0 && (
              <div className="flex items-center gap-3">
                {social.map((link) => {
                  const Icon = socialIcons[link.platform] || Github;
                  return (
                    <a
                      key={link.platform}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        "w-9 h-9 rounded-lg flex items-center justify-center",
                        "bg-muted hover:bg-muted/80",
                        "text-muted-foreground hover:text-foreground",
                        "border border-border hover:border-border-hover",
                        "transition-all duration-200"
                      )}
                      aria-label={`Follow us on ${link.platform}`}
                    >
                      <Icon className="w-4 h-4" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Navigation columns */}
          {navigation.map((section) => (
            <div key={section.title}>
              <h4 className="text-foreground font-semibold text-sm mb-4">
                {section.title}
              </h4>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      className={cn(
                        "text-muted-foreground text-sm",
                        "hover:text-foreground",
                        "transition-colors duration-200"
                      )}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div
          className={cn(
            "mt-16 pt-8",
            "border-t border-border",
            "flex flex-col sm:flex-row items-center justify-between gap-4"
          )}
        >
          <p className="text-muted-foreground text-sm">{company.copyright}</p>
          <div className="flex items-center gap-6">
            <a
              href="/privacy"
              className="text-muted-foreground text-sm hover:text-foreground transition-colors"
            >
              Privacy
            </a>
            <a
              href="/terms"
              className="text-muted-foreground text-sm hover:text-foreground transition-colors"
            >
              Terms
            </a>
            <a
              href="/cookies"
              className="text-muted-foreground text-sm hover:text-foreground transition-colors"
            >
              Cookies
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
